import { Stack, Toolbar, Box } from '@mui/material';
import EditorDrawer from './EditorDrawer';
import type { CodeSnippetInsertionOutcome, CodeSnippetInsertionPayload, EditorState } from './types';
import * as monaco from 'monaco-editor';
import { ReactNode } from 'react';
import { LAYOUT_CONSTANTS } from '../../utils/layout-constants';
import type { ICardComponent } from './Devices/DeviceCardBuilder';

interface CodeEditorLayoutProps {
  readOnly: boolean;
  leftDrawer: boolean;
  drawerWidth: number;
  editorInstance: monaco.editor.IStandaloneCodeEditor | null;
  editorState: EditorState;
  onLeftDrawerToggle: () => void;
  children: ReactNode;
  hardwareDynamicCards: ICardComponent[];
  hardwareIsLoading: boolean;
  driveUnitPortRegistryRev: number;
  onInsertSnippet: (payload: CodeSnippetInsertionPayload) => CodeSnippetInsertionOutcome;
}

export default function CodeEditorLayout({
  readOnly,
  leftDrawer,
  drawerWidth,
  editorInstance,
  editorState,
  children,
  hardwareDynamicCards,
  hardwareIsLoading,
  driveUnitPortRegistryRev,
  onInsertSnippet,
}: CodeEditorLayoutProps) {
  return (
    <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 0, minWidth: 0 }}>
      {/* Unified Editor Drawer: Contains Config and Snippets vertical tabs */}
      {/* Config tab shows: Mechanisms, Drivebase, Controller, Control Mode */}
      {/* Snippets tab shows: Actions, API, Electronics, Mechanisms */}
      <EditorDrawer
        drawerWidth={drawerWidth}
        isOpen={readOnly ? false : leftDrawer}
        editor={editorInstance}
        editorState={editorState}
        hardwareDynamicCards={hardwareDynamicCards}
        hardwareIsLoading={hardwareIsLoading}
        driveUnitPortRegistryRev={driveUnitPortRegistryRev}
        onInsertSnippet={onInsertSnippet}
      />

      {/* Main Content Area: Monaco Editor and Bottom Drawer */}
      <Stack
        direction="column"
        component="main"
        minWidth={0}
        minHeight={0}
        height="100%"
        sx={{
          flexGrow: 1,
          bgcolor: 'background.default',
          // Ensure editor content can scroll above the bottom drawer (fallback to control bar height)
          pb: `var(--bottom-drawer-offset, ${LAYOUT_CONSTANTS.BOTTOM_CONTROL_BAR_HEIGHT}px)`,
        }}
      >
        <Toolbar />
        {children}
      </Stack>
    </Box>
  );
}
